import * as THREE from 'three';
import { MASK } from '../world/Physics.js';

/**
 * Navigation without an authoring tool.
 *
 * The level is generated at runtime, so there is no navmesh to load. Instead we
 * sample a grid once at load and A* over it. Two things make that grid actually
 * usable in this level rather than merely present:
 *
 * 1. **Layers.** A flat height-per-cell grid would see only the warehouse roof and
 *    decide the floor beneath it does not exist. Each cell therefore stores up to
 *    three standable surfaces — street, mezzanine, roof — and A* treats them as
 *    separate nodes joined only where the step between them is climbable.
 *
 * 2. **Funnelling.** A raw grid path is a staircase, which reads as robotic the
 *    moment an agent walks it. Waypoints are greedily merged while the straight
 *    line between them stays walkable, collapsing forty cells into three corners.
 *
 * Local steering (separation + obstacle whiskers) rides on top so agents flow
 * around each other instead of shoving.
 */

export const TUNING = {
  cell: 0.75,
  layers: 4, // ground + stair tread + mezzanine + roof all coexist in one cell
  agentRadius: 0.36,
  agentHeight: 1.75,
  clearanceBottom: 0.3, // kerb-height clutter is stepped over, not avoided
  // Clearance is tested in two bands. Around the shins the probe is narrow, so a
  // 0.3 m stair tread two steps ahead does not read as a wall; from the waist up
  // it is full width, so doorways and gaps still have to fit a body.
  lowRadius: 0.14,
  lowBandTop: 0.78,
  maxStep: 0.52,
  // A 45-degree staircase climbs a whole cell width per cell, far past maxStep,
  // so those links are found by explicitly probing for a continuous ramp.
  climbMax: 1.25,
  stairProbe: 0.22, // metres between ramp samples
  stairRiser: 0.44, // biggest single riser the capsule sweep can step up
  dropMax: 3.2, // a soldier will step off a ledge this high, but not vault back up
  dropCost: 3.0, // ...and prefers stairs when there are any
  layerMerge: 0.35, // surfaces closer than this are the same floor
  maxCells: 40000,
  maxAStarNodes: 6000,
  pathCacheSize: 96,

  separationRadius: 1.35,
  separationForce: 2.6,
  avoidLook: 1.5,
  avoidForce: 2.2,
};

/* --------------------------- binary min-heap --------------------------- */

class Heap {
  constructor() {
    this.ids = [];
    this.keys = [];
  }
  clear() {
    this.ids.length = 0;
    this.keys.length = 0;
  }
  get size() {
    return this.ids.length;
  }
  push(id, key) {
    const ids = this.ids;
    const keys = this.keys;
    let i = ids.length;
    ids.push(id);
    keys.push(key);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (keys[p] <= keys[i]) break;
      const ti = ids[p];
      ids[p] = ids[i];
      ids[i] = ti;
      const tk = keys[p];
      keys[p] = keys[i];
      keys[i] = tk;
      i = p;
    }
  }
  pop() {
    const ids = this.ids;
    const keys = this.keys;
    const top = ids[0];
    const lastId = ids.pop();
    const lastKey = keys.pop();
    if (ids.length) {
      ids[0] = lastId;
      keys[0] = lastKey;
      let i = 0;
      const n = ids.length;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < n && keys[l] < keys[m]) m = l;
        if (r < n && keys[r] < keys[m]) m = r;
        if (m === i) break;
        const ti = ids[m];
        ids[m] = ids[i];
        ids[i] = ti;
        const tk = keys[m];
        keys[m] = keys[i];
        keys[i] = tk;
        i = m;
      }
    }
    return top;
  }
}

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const NEIGH = new Int8Array([1, 0, -1, 0, 0, 1, 0, -1, 1, 1, 1, -1, -1, 1, -1, -1]);
const WHISKERS = [
  { a: 0, w: 1.0 },
  { a: 0.55, w: 0.6 },
  { a: -0.55, w: 0.6 },
];

export class Nav {
  constructor(ctx) {
    this.ctx = ctx;
    this.physics = ctx.physics;
    this.ready = false;
    this.buildMs = 0;
    this.walkableCount = 0;

    this._heap = new Heap();
    this._pathCache = new Map();
    this._buckets = new Map();
    this._huge = [];
    this._bucketSize = 6;
    this._scratch = [];
    this._tops = [];
    this._tops2 = [];
    this.stairLinks = new Map();
    this.dropLinks = new Map();
    this.dropLinksRev = new Map();
  }

  /* ------------------------------- build ------------------------------- */

  build(level) {
    const t0 = performance.now();
    const bounds = this._resolveBounds(level);
    this.min = bounds.min;
    this.max = bounds.max;

    const K = (this.K = TUNING.layers);
    let cell = TUNING.cell;
    let nx = Math.ceil((this.max.x - this.min.x) / cell);
    let nz = Math.ceil((this.max.z - this.min.z) / cell);
    while (nx * nz > TUNING.maxCells) {
      cell *= 1.2;
      nx = Math.ceil((this.max.x - this.min.x) / cell);
      nz = Math.ceil((this.max.z - this.min.z) / cell);
    }
    this.cell = cell;
    this.nx = nx;
    this.nz = nz;

    const nodes = nx * nz * K;
    this.walk = new Uint8Array(nodes);
    this.h = new Float32Array(nodes);
    this.cost = new Float32Array(nodes);
    this.layerCount = new Uint8Array(nx * nz);
    this._g = new Float32Array(nodes);
    this._from = new Int32Array(nodes);
    this._stamp = new Int32Array(nodes);
    this._closed = new Uint8Array(nodes);
    this._epoch = 0;

    this._indexColliders();

    let walkable = 0;
    const tops = this._tops;
    for (let z = 0; z < nz; z++) {
      for (let x = 0; x < nx; x++) {
        const wx = this.min.x + (x + 0.5) * cell;
        const wz = this.min.z + (z + 0.5) * cell;
        this._surfacesAt(wx, wz, tops);
        const cellI = z * nx + x;
        let layer = 0;
        let lastAccepted = -Infinity;
        for (let t = 0; t < tops.length && layer < K; t++) {
          const gy = tops[t];
          // Merge near-coincident surfaces only *after* the clearance test.
          // Merging first silently deletes stair treads: a 0.22 m riser sits
          // within the merge window of the floor below it, and the floor is the
          // one without headroom, so the whole staircase would vanish.
          if (!this._hasClearance(wx, wz, gy)) continue;
          if (gy - lastAccepted < TUNING.layerMerge) continue;
          lastAccepted = gy;
          const n = cellI * K + layer;
          this.h[n] = gy;
          this.walk[n] = 1;
          layer++;
          walkable++;
        }
        this.layerCount[cellI] = layer;
      }
    }

    // Soft cost near geometry so agents do not scrape along every wall.
    for (let z = 0; z < nz; z++) {
      for (let x = 0; x < nx; x++) {
        const cellI = z * nx + x;
        for (let l = 0; l < this.layerCount[cellI]; l++) {
          const n = cellI * K + l;
          let open = 0;
          for (let d = 0; d < 8; d++) {
            if (this._neighbour(x + NEIGH[d * 2], z + NEIGH[d * 2 + 1], this.h[n]) >= 0) open++;
          }
          this.cost[n] = open >= 8 ? 0 : (8 - open) * 0.2;
        }
      }
    }

    this._buildStairLinks();
    this._buildDropLinks();
    this._buildComponents();
    this._buildReachability();

    this.walkableCount = walkable;
    this.ready = walkable > 8;
    this.buildMs = performance.now() - t0;
    return this;
  }

  /**
   * Stairs climb faster than a grid cell can express. For every neighbour pair
   * whose height gap is too big for a step but small enough to be a staircase,
   * probe the line between them: if the surfaces underneath form a monotone
   * chain of capsule-climbable risers, record the link explicitly.
   *
   * Without this, every roof, mezzanine and upper floor in the level is an
   * island and a third of the squad can never reach the player.
   */
  _buildStairLinks() {
    this.stairLinks = new Map();
    const K = this.K;
    const nx = this.nx;
    const nz = this.nz;
    let found = 0;
    for (let z = 0; z < nz; z++) {
      for (let x = 0; x < nx; x++) {
        const cellI = z * nx + x;
        for (let l = 0; l < this.layerCount[cellI]; l++) {
          const a = cellI * K + l;
          const ay = this.h[a];
          for (let d = 0; d < 8; d++) {
            const bx = x + NEIGH[d * 2];
            const bz = z + NEIGH[d * 2 + 1];
            if (bx < 0 || bz < 0 || bx >= nx || bz >= nz) continue;
            const bCell = bz * nx + bx;
            for (let m = 0; m < this.layerCount[bCell]; m++) {
              const b = bCell * K + m;
              const dy = this.h[b] - ay;
              // Only upward links are probed; the reverse is added at the same time.
              if (dy <= TUNING.maxStep || dy > TUNING.climbMax) continue;
              if (!this._rampBetween(a, b)) continue;
              this._addStairLink(a, b);
              this._addStairLink(b, a);
              found++;
            }
          }
        }
      }
    }
    this.stairLinkCount = found;
  }

  /**
   * One-way drops. Gravity is free: a soldier on a roof can step off a 3 m ledge
   * even though he could never climb back up. Modelling that as a directed edge
   * is what lets rooftop enemies come down and join the fight instead of pacing
   * their island forever.
   */
  _buildDropLinks() {
    this.dropLinks = new Map();
    this.dropLinksRev = new Map();
    const K = this.K;
    const nx = this.nx;
    const nz = this.nz;
    let found = 0;
    for (let z = 0; z < nz; z++) {
      for (let x = 0; x < nx; x++) {
        const cellI = z * nx + x;
        for (let l = 0; l < this.layerCount[cellI]; l++) {
          const a = cellI * K + l;
          const ay = this.h[a];
          for (let d = 0; d < 8; d++) {
            const bx = x + NEIGH[d * 2];
            const bz = z + NEIGH[d * 2 + 1];
            if (bx < 0 || bz < 0 || bx >= nx || bz >= nz) continue;
            const bCell = bz * nx + bx;
            for (let m = 0; m < this.layerCount[bCell]; m++) {
              const b = bCell * K + m;
              const fall = ay - this.h[b];
              if (fall <= TUNING.maxStep || fall > TUNING.dropMax) continue;
              // Already handled as a staircase? Then it is not a drop.
              const st = this.stairLinks.get(a);
              if (st && st.includes(b)) continue;
              // The landing must be the first surface below, or they would clip
              // through a floor on the way down.
              this._surfacesAt(this.wxOf(b), this.wzOf(b), this._tops2);
              let occluded = false;
              for (let i = 0; i < this._tops2.length; i++) {
                const s = this._tops2[i];
                if (s > this.h[b] + 0.1 && s < ay - 0.1) {
                  occluded = true;
                  break;
                }
              }
              if (occluded) continue;
              let list = this.dropLinks.get(a);
              if (!list) this.dropLinks.set(a, (list = []));
              if (!list.includes(b)) list.push(b);
              let rev = this.dropLinksRev.get(b);
              if (!rev) this.dropLinksRev.set(b, (rev = []));
              if (!rev.includes(a)) rev.push(a);
              found++;
            }
          }
        }
      }
    }
    this.dropLinkCount = found;
  }

  /**
   * Which nodes can actually *get to* the main area. Because drops are one-way,
   * this is a reverse flood from the main component, not a symmetric component
   * test. The director uses it to refuse to spawn a soldier somewhere he could
   * never walk off — cheap insurance against level geometry changing.
   */
  _buildReachability() {
    const total = this.nx * this.nz * this.K;
    this.reachesMain = new Uint8Array(total);
    const K = this.K;
    const nx = this.nx;
    const queue = new Int32Array(total);
    let head = 0;
    let tail = 0;
    for (let n = 0; n < total; n++) {
      if (this.walk[n] === 1 && this.comp[n] === this.mainComponent) {
        this.reachesMain[n] = 1;
        queue[tail++] = n;
      }
    }
    while (head < tail) {
      const cur = queue[head++];
      const ci = (cur / K) | 0;
      const cx = ci % nx;
      const cz = (ci / nx) | 0;
      const cy = this.h[cur];
      // Symmetric edges: walk them backwards by walking them forwards.
      for (let d = 0; d < 8; d++) {
        const n = this._neighbour(cx + NEIGH[d * 2], cz + NEIGH[d * 2 + 1], cy);
        if (n < 0 || this.reachesMain[n]) continue;
        this.reachesMain[n] = 1;
        queue[tail++] = n;
      }
      const st = this.stairLinks.get(cur);
      if (st) {
        for (let i = 0; i < st.length; i++) {
          if (this.reachesMain[st[i]]) continue;
          this.reachesMain[st[i]] = 1;
          queue[tail++] = st[i];
        }
      }
      // Drops are directed: anyone who can fall *onto* this node can reach main.
      const rev = this.dropLinksRev.get(cur);
      if (rev) {
        for (let i = 0; i < rev.length; i++) {
          if (this.reachesMain[rev[i]]) continue;
          this.reachesMain[rev[i]] = 1;
          queue[tail++] = rev[i];
        }
      }
    }
    let count = 0;
    for (let n = 0; n < total; n++) if (this.reachesMain[n]) count++;
    this.reachableCount = count;
  }

  /** Can an agent standing here reach the main play area at all? */
  canReachMain(pos) {
    if (!this.ready) return true;
    const n = this.nodeAt(pos);
    return n >= 0 && this.reachesMain[n] === 1;
  }

  _addStairLink(a, b) {
    let list = this.stairLinks.get(a);
    if (!list) this.stairLinks.set(a, (list = []));
    if (!list.includes(b)) list.push(b);
  }

  /** Is the ground between two nodes a climbable, monotone ramp? */
  _rampBetween(a, b) {
    const ax = this.wxOf(a);
    const az = this.wzOf(a);
    const bx = this.wxOf(b);
    const bz = this.wzOf(b);
    const ay = this.h[a];
    const by = this.h[b];
    const dist = Math.hypot(bx - ax, bz - az);
    const steps = Math.max(3, Math.ceil(dist / TUNING.stairProbe));
    let prev = ay;
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const px = ax + (bx - ax) * t;
      const pz = az + (bz - az) * t;
      const expect = ay + (by - ay) * t;
      this._surfacesAt(px, pz, this._tops2);
      // Take the surface nearest the ideal ramp height.
      let best = null;
      let bestD = 0.42;
      for (let i = 0; i < this._tops2.length; i++) {
        const dd = Math.abs(this._tops2[i] - expect);
        if (dd < bestD) {
          bestD = dd;
          best = this._tops2[i];
        }
      }
      if (best === null) return false;
      if (Math.abs(best - prev) > TUNING.stairRiser) return false;
      if (!this._hasClearance(px, pz, best)) return false;
      prev = best;
    }
    return Math.abs(prev - by) <= TUNING.stairRiser;
  }

  /**
   * Flood-fill connectivity. Lets the director refuse to spawn a soldier onto an
   * island he could never walk off — a defence against the level geometry
   * changing underneath us.
   */
  _buildComponents() {
    const total = this.nx * this.nz * this.K;
    this.comp = new Int32Array(total).fill(-1);
    const K = this.K;
    const nx = this.nx;
    const queue = new Int32Array(total);
    this.compSizes = [];
    let next = 0;
    for (let start = 0; start < total; start++) {
      if (this.walk[start] !== 1 || this.comp[start] !== -1) continue;
      const id = next++;
      let head = 0;
      let tail = 0;
      queue[tail++] = start;
      this.comp[start] = id;
      let size = 0;
      while (head < tail) {
        const cur = queue[head++];
        size++;
        const ci = (cur / K) | 0;
        const cx = ci % nx;
        const cz = (ci / nx) | 0;
        const cy = this.h[cur];
        for (let d = 0; d < 8; d++) {
          const n = this._neighbour(cx + NEIGH[d * 2], cz + NEIGH[d * 2 + 1], cy);
          if (n < 0 || this.comp[n] !== -1) continue;
          this.comp[n] = id;
          queue[tail++] = n;
        }
        const links = this.stairLinks.get(cur);
        if (links) {
          for (let i = 0; i < links.length; i++) {
            const n = links[i];
            if (this.comp[n] !== -1) continue;
            this.comp[n] = id;
            queue[tail++] = n;
          }
        }
      }
      this.compSizes.push(size);
    }
    let best = -1;
    let bestSize = -1;
    for (let i = 0; i < this.compSizes.length; i++) {
      if (this.compSizes[i] > bestSize) {
        bestSize = this.compSizes[i];
        best = i;
      }
    }
    this.mainComponent = best;
    this.componentCount = next;
  }

  /** Component id of the surface under a world position, or -1. */
  componentAt(pos) {
    const n = this.nodeAt(pos);
    return n >= 0 ? this.comp[n] : -1;
  }

  /** Can an agent at `from` walk to `to` at all? Cheap: no path is solved. */
  connected(from, to) {
    if (!this.ready) return true;
    const a = this.componentAt(from);
    const b = this.componentAt(to);
    return a >= 0 && a === b;
  }

  _resolveBounds(level) {
    const min = new THREE.Vector3(-60, -4, -60);
    const max = new THREE.Vector3(60, 30, 60);
    const nb = level?.navBounds;
    if (nb) {
      const lo = nb.min ?? nb.lo;
      const hi = nb.max ?? nb.hi;
      if (lo && hi) {
        min.set(lo.x, lo.y ?? -4, lo.z);
        max.set(hi.x, hi.y ?? 30, hi.z);
        return { min, max };
      }
    }
    const cols = this.physics.colliders;
    if (cols && cols.length) {
      min.set(Infinity, Infinity, Infinity);
      max.set(-Infinity, -Infinity, -Infinity);
      for (const c of cols) {
        min.min(c.min);
        max.max(c.max);
      }
      const cx = (min.x + max.x) * 0.5;
      const cz = (min.z + max.z) * 0.5;
      const half = Math.min(90, Math.max(20, Math.max(max.x - min.x, max.z - min.z) * 0.5));
      min.x = cx - half;
      max.x = cx + half;
      min.z = cz - half;
      max.z = cz + half;
      max.y = Math.min(max.y + 2, min.y + 60);
    }
    return { min, max };
  }

  /** Our own coarse index over the physics collider list (public data only). */
  _indexColliders() {
    this._buckets.clear();
    this._huge.length = 0;
    const S = this._bucketSize;
    const cols = this.physics.colliders ?? [];
    for (const c of cols) {
      if (!(c.mask & (MASK.WORLD | MASK.PROP))) continue;
      const x0 = Math.floor(c.min.x / S);
      const x1 = Math.floor(c.max.x / S);
      const z0 = Math.floor(c.min.z / S);
      const z1 = Math.floor(c.max.z / S);
      if ((x1 - x0 + 1) * (z1 - z0 + 1) > 900) {
        // A ground plane belongs in every bucket; keep those in one global list.
        this._huge.push(c);
        continue;
      }
      for (let z = z0; z <= z1; z++) {
        for (let x = x0; x <= x1; x++) {
          const k = x * 73856093 ^ z * 19349663;
          let b = this._buckets.get(k);
          if (!b) this._buckets.set(k, (b = []));
          b.push(c);
        }
      }
    }
  }

  /** Standable surface heights at an XZ point, ascending and de-duplicated. */
  _surfacesAt(x, z, out) {
    out.length = 0;
    const S = this._bucketSize;
    const b = this._buckets.get(Math.floor(x / S) * 73856093 ^ Math.floor(z / S) * 19349663);
    const loY = this.min.y - 1;
    const hiY = this.max.y;
    const consider = (c) => {
      if (x < c.min.x || x > c.max.x || z < c.min.z || z > c.max.z) return;
      const y = c.max.y;
      if (y < loY || y > hiY) return;
      out.push(y);
    };
    if (b) for (let i = 0; i < b.length; i++) consider(b[i]);
    for (let i = 0; i < this._huge.length; i++) consider(this._huge[i]);
    if (out.length < 2) return out;
    out.sort((p, q) => p - q);
    // Only strip exact duplicates here; real merging happens after the clearance
    // test, which is the only place that knows which surface is the usable one.
    let w = 1;
    for (let i = 1; i < out.length; i++) {
      if (out[i] - out[w - 1] > 0.06) out[w++] = out[i];
    }
    out.length = w;
    return out;
  }

  /** Can a standing capsule occupy this spot, with its feet on `y`? */
  _hasClearance(x, z, y) {
    if (this._blocked(x, z, TUNING.lowRadius, y + TUNING.clearanceBottom, y + TUNING.lowBandTop)) return false;
    return !this._blocked(x, z, TUNING.agentRadius, y + TUNING.lowBandTop, y + TUNING.agentHeight);
  }

  _blocked(x, z, r, y0, y1) {
    const S = this._bucketSize;
    const x0 = Math.floor((x - r) / S);
    const x1 = Math.floor((x + r) / S);
    const z0 = Math.floor((z - r) / S);
    const z1 = Math.floor((z + r) / S);
    for (let bz = z0; bz <= z1; bz++) {
      for (let bx = x0; bx <= x1; bx++) {
        const b = this._buckets.get(bx * 73856093 ^ bz * 19349663);
        if (!b) continue;
        for (let i = 0; i < b.length; i++) {
          const c = b[i];
          if (y1 <= c.min.y || y0 >= c.max.y) continue;
          if (x + r <= c.min.x || x - r >= c.max.x) continue;
          if (z + r <= c.min.z || z - r >= c.max.z) continue;
          return true;
        }
      }
    }
    for (let i = 0; i < this._huge.length; i++) {
      const c = this._huge[i];
      if (y1 <= c.min.y || y0 >= c.max.y) continue;
      if (x + r <= c.min.x || x - r >= c.max.x) continue;
      if (z + r <= c.min.z || z - r >= c.max.z) continue;
      return true;
    }
    return false;
  }

  /* ------------------------------ accessors ---------------------------- */

  gx(wx) {
    return Math.floor((wx - this.min.x) / this.cell);
  }
  gz(wz) {
    return Math.floor((wz - this.min.z) / this.cell);
  }
  wxOf(node) {
    const cellI = (node / this.K) | 0;
    return this.min.x + ((cellI % this.nx) + 0.5) * this.cell;
  }
  wzOf(node) {
    const cellI = (node / this.K) | 0;
    return this.min.z + (((cellI / this.nx) | 0) + 0.5) * this.cell;
  }

  /** Node in cell (x,z) whose surface is nearest `y` and within a climbable step. */
  _neighbour(x, z, y) {
    if (x < 0 || z < 0 || x >= this.nx || z >= this.nz) return -1;
    const cellI = z * this.nx + x;
    const n0 = cellI * this.K;
    let best = -1;
    let bestD = TUNING.maxStep;
    for (let l = 0; l < this.layerCount[cellI]; l++) {
      const d = Math.abs(this.h[n0 + l] - y);
      if (d <= bestD) {
        bestD = d;
        best = n0 + l;
      }
    }
    return best;
  }

  /** The node a world position is standing on (or just above). */
  nodeAt(pos) {
    if (!this.ready) return -1;
    const x = this.gx(pos.x);
    const z = this.gz(pos.z);
    if (x < 0 || z < 0 || x >= this.nx || z >= this.nz) return -1;
    const cellI = z * this.nx + x;
    const n0 = cellI * this.K;
    let best = -1;
    let bestD = 2.6;
    for (let l = 0; l < this.layerCount[cellI]; l++) {
      const dy = pos.y - this.h[n0 + l];
      const d = dy >= -0.6 ? dy : -dy * 3; // strongly prefer the floor below us
      if (d < bestD) {
        bestD = d;
        best = n0 + l;
      }
    }
    return best;
  }

  isWalkable(pos) {
    return this.nodeAt(pos) >= 0;
  }

  groundAt(pos) {
    const n = this.nodeAt(pos);
    return n >= 0 ? this.h[n] : pos.y;
  }

  _nodeToVec(node, out) {
    return out.set(this.wxOf(node), this.h[node], this.wzOf(node));
  }

  /** Nearest walkable surface to a world position, searched in rings. */
  nearestWalkable(pos, maxRings = 8, out = new THREE.Vector3()) {
    if (!this.ready) return out.copy(pos);
    const here = this.nodeAt(pos);
    if (here >= 0) return this._nodeToVec(here, out);
    const cx = this.gx(pos.x);
    const cz = this.gz(pos.z);
    let best = -1;
    let bestD = Infinity;
    for (let r = 1; r <= maxRings; r++) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
          const x = cx + dx;
          const z = cz + dz;
          if (x < 0 || z < 0 || x >= this.nx || z >= this.nz) continue;
          const cellI = z * this.nx + x;
          const n0 = cellI * this.K;
          for (let l = 0; l < this.layerCount[cellI]; l++) {
            const d = Math.abs(this.h[n0 + l] - pos.y) * 2 + r * this.cell;
            if (d < bestD) {
              bestD = d;
              best = n0 + l;
            }
          }
        }
      }
      if (best >= 0) return this._nodeToVec(best, out);
    }
    return out.copy(pos);
  }

  randomWalkable(rng, ref = null, minDist = 0, maxDist = Infinity, tries = 40, out = new THREE.Vector3()) {
    if (!this.ready) return null;
    for (let t = 0; t < tries; t++) {
      const x = Math.floor(rng() * this.nx);
      const z = Math.floor(rng() * this.nz);
      const cellI = z * this.nx + x;
      const lc = this.layerCount[cellI];
      if (!lc) continue;
      const node = cellI * this.K + Math.floor(rng() * lc);
      const px = this.wxOf(node);
      const pz = this.wzOf(node);
      if (ref) {
        const d = Math.hypot(px - ref.x, pz - ref.z);
        if (d < minDist || d > maxDist) continue;
      }
      return out.set(px, this.h[node], pz);
    }
    return null;
  }

  /* ------------------------------- A* --------------------------------- */

  findPath(from, to, out = []) {
    out.length = 0;
    if (!this.ready) return null;

    let start = this.nodeAt(from);
    if (start < 0) {
      this.nearestWalkable(from, 5, _v);
      start = this.nodeAt(_v);
    }
    let goal = this.nodeAt(to);
    if (goal < 0) {
      this.nearestWalkable(to, 8, _v);
      goal = this.nodeAt(_v);
    }
    if (start < 0 || goal < 0) return null;
    if (start === goal) {
      out.push(new THREE.Vector3(to.x, this.h[goal], to.z));
      return out;
    }

    const key = start * 1000003 + goal;
    const cached = this._pathCache.get(key);
    if (cached && cached.t > performance.now() - 2500) {
      for (const p of cached.path) out.push(p.clone());
      return out;
    }

    const K = this.K;
    const nx = this.nx;
    const cell = this.cell;
    const epoch = ++this._epoch;
    const heap = this._heap;
    heap.clear();

    const gCellI = (goal / K) | 0;
    const hx = gCellI % nx;
    const hz = (gCellI / nx) | 0;
    const gy = this.h[goal];
    const heur = (n) => {
      const ci = (n / K) | 0;
      const dx = Math.abs((ci % nx) - hx);
      const dz = Math.abs(((ci / nx) | 0) - hz);
      return (Math.max(dx, dz) + 0.414 * Math.min(dx, dz)) * cell + Math.abs(this.h[n] - gy) * 0.4;
    };

    this._stamp[start] = epoch;
    this._g[start] = 0;
    this._from[start] = -1;
    this._closed[start] = 0;
    heap.push(start, heur(start));

    let expanded = 0;
    let found = false;
    while (heap.size) {
      const cur = heap.pop();
      if (this._stamp[cur] === epoch && this._closed[cur] === 1) continue;
      this._closed[cur] = 1;
      this._stamp[cur] = epoch;
      if (cur === goal) {
        found = true;
        break;
      }
      if (++expanded > TUNING.maxAStarNodes) break;

      const ci = (cur / K) | 0;
      const cxx = ci % nx;
      const czz = (ci / nx) | 0;
      const cy = this.h[cur];
      const gCur = this._g[cur];

      for (let d = 0; d < 8; d++) {
        const dx = NEIGH[d * 2];
        const dz = NEIGH[d * 2 + 1];
        const n = this._neighbour(cxx + dx, czz + dz, cy);
        if (n < 0) continue;
        if (dx !== 0 && dz !== 0) {
          // No corner cutting: both orthogonal neighbours must be open too.
          if (this._neighbour(cxx + dx, czz, cy) < 0) continue;
          if (this._neighbour(cxx, czz + dz, cy) < 0) continue;
        }
        if (this._stamp[n] === epoch && this._closed[n] === 1) continue;
        const step = (dx !== 0 && dz !== 0 ? 1.4142 : 1) * cell;
        const ng = gCur + step + this.cost[n] * cell + Math.abs(this.h[n] - cy) * 1.5;
        if (this._stamp[n] === epoch && ng >= this._g[n]) continue;
        this._stamp[n] = epoch;
        this._closed[n] = 0;
        this._g[n] = ng;
        this._from[n] = cur;
        heap.push(n, ng + heur(n) * 1.1);
      }

      // Staircases: links the grid step rule cannot express.
      const links = this.stairLinks.get(cur);
      if (links) {
        for (let i = 0; i < links.length; i++) {
          const n = links[i];
          if (this._stamp[n] === epoch && this._closed[n] === 1) continue;
          const rise = Math.abs(this.h[n] - cy);
          // Stairs are slower than flat ground, and going up costs more than down.
          const ng = gCur + cell * 1.3 + rise * (this.h[n] > cy ? 2.2 : 1.0);
          if (this._stamp[n] === epoch && ng >= this._g[n]) continue;
          this._stamp[n] = epoch;
          this._closed[n] = 0;
          this._g[n] = ng;
          this._from[n] = cur;
          heap.push(n, ng + heur(n) * 1.1);
        }
      }

      // One-way drops, priced high enough that a staircase always wins.
      const drops = this.dropLinks.get(cur);
      if (drops) {
        for (let i = 0; i < drops.length; i++) {
          const n = drops[i];
          if (this._stamp[n] === epoch && this._closed[n] === 1) continue;
          const ng = gCur + cell + TUNING.dropCost + (cy - this.h[n]) * 0.8;
          if (this._stamp[n] === epoch && ng >= this._g[n]) continue;
          this._stamp[n] = epoch;
          this._closed[n] = 0;
          this._g[n] = ng;
          this._from[n] = cur;
          heap.push(n, ng + heur(n) * 1.1);
        }
      }
    }

    if (!found) return null;

    const raw = this._scratch;
    raw.length = 0;
    let c = goal;
    let guard = 0;
    while (c !== -1 && guard++ < 4000) {
      raw.push(c);
      c = this._from[c];
    }
    raw.reverse();

    this._funnel(raw, out, to);

    const store = out.map((p) => p.clone());
    if (this._pathCache.size > TUNING.pathCacheSize) this._pathCache.clear();
    this._pathCache.set(key, { t: performance.now(), path: store });
    return out;
  }

  _funnel(raw, out, finalTarget) {
    if (raw.length <= 1) {
      out.push(new THREE.Vector3(finalTarget.x, raw.length ? this.h[raw[0]] : finalTarget.y, finalTarget.z));
      return;
    }
    let anchor = 0;
    let guard = 0;
    while (anchor < raw.length - 1 && guard++ < 64) {
      let best = anchor + 1;
      for (let j = anchor + 2; j < raw.length; j++) {
        if (this._lineWalkable(raw[anchor], raw[j])) best = j;
        else break; // the first failure ends the straight run
      }
      const n = raw[best];
      out.push(new THREE.Vector3(this.wxOf(n), this.h[n], this.wzOf(n)));
      anchor = best;
    }
    if (out.length) {
      const last = out[out.length - 1];
      if (Math.hypot(last.x - finalTarget.x, last.z - finalTarget.z) < this.cell * 2) {
        last.set(finalTarget.x, last.y, finalTarget.z);
      }
    }
  }

  /** Straight-line walkability between two nodes, following surface layers. */
  _lineWalkable(a, b) {
    const ax = this.wxOf(a);
    const az = this.wzOf(a);
    const bx = this.wxOf(b);
    const bz = this.wzOf(b);
    const dx = bx - ax;
    const dz = bz - az;
    const dist = Math.hypot(dx, dz);
    const steps = Math.ceil(dist / (this.cell * 0.65));
    let y = this.h[a];
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const n = this._neighbour(this.gx(ax + dx * t), this.gz(az + dz * t), y);
      if (n < 0) return false;
      y = this.h[n];
    }
    return Math.abs(y - this.h[b]) <= TUNING.maxStep;
  }

  directWalkable(from, to) {
    if (!this.ready) return true;
    const a = this.nodeAt(from);
    const b = this.nodeAt(to);
    if (a < 0 || b < 0) return false;
    return this._lineWalkable(a, b);
  }

  /* ----------------------------- steering ------------------------------ */

  /**
   * Seek blended with separation from the rest of the squad and obstacle
   * whiskers. Whisker results are cached on the agent so the raycasts can be
   * time-sliced by the caller without the steering going jittery.
   */
  steer(agent, desiredDir, others, out, whiskers = true) {
    out.copy(desiredDir);

    let sx = 0;
    let sz = 0;
    const R = TUNING.separationRadius;
    for (const o of others) {
      if (o === agent || o.dead) continue;
      const dx = agent.position.x - o.position.x;
      const dy = agent.position.y - o.position.y;
      if (dy > 1.6 || dy < -1.6) continue; // different floor: not in the way
      const dz = agent.position.z - o.position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > R * R || d2 < 1e-5) continue;
      const d = Math.sqrt(d2);
      const w = (1 - d / R) / d;
      sx += dx * w;
      sz += dz * w;
    }
    out.x += sx * TUNING.separationForce;
    out.z += sz * TUNING.separationForce;

    if (whiskers) {
      const look = TUNING.avoidLook;
      const eye = _v.set(agent.position.x, agent.position.y + 0.9, agent.position.z);
      const base = Math.atan2(out.x, out.z);
      let ax = 0;
      let az = 0;
      for (const off of WHISKERS) {
        const a = base + off.a;
        _v2.set(Math.sin(a), 0, Math.cos(a));
        const hit = this.physics.raycast(eye, _v2, look, MASK.WORLD | MASK.PROP);
        if (hit.hit) {
          const push = (1 - hit.dist / look) * TUNING.avoidForce * off.w;
          ax += hit.normal.x * push;
          az += hit.normal.z * push;
        }
      }
      agent._avoidX = ax;
      agent._avoidZ = az;
    }
    out.x += agent._avoidX || 0;
    out.z += agent._avoidZ || 0;

    out.y = 0;
    const len = Math.hypot(out.x, out.z);
    if (len > 1e-5) {
      out.x /= len;
      out.z /= len;
    }
    return out;
  }

  dispose() {
    this._pathCache.clear();
    this._buckets.clear();
    this._huge.length = 0;
  }
}
